import { useState, forwardRef, useImperativeHandle, useRef, useId, useEffect } from 'react';

import type { ChangeEvent } from 'react';

import styles from './AddTableFrom.module.css';

import { Button, FormField, InputField, Label } from 'components';
import { useTranslation } from 'components/RteEditor/hooks/useTranslation';
import { removeScrollableContainers, setScrollableContainers } from 'components/RteEditor/helpers';

interface AddTableFormProps {
  onCloseHandler: () => void;
  onSaveHandler: (nrfOfRows: number, nrOfColumns: number) => void;
}

export interface addTableForm {
  clearForm: () => void;
  focusFirstField: () => void;
}

export const AddTableForm = forwardRef(({ onCloseHandler, onSaveHandler }: AddTableFormProps, ref) => {
  const t = useTranslation();
  const [rowCount, setRowCount] = useState<number>();
  const [columnCount, setColumnCount] = useState<number>();
  const firstField = useRef<HTMLInputElement>(null);
  const rowId = `row_${useId()}`;
  const columnId = `column_${useId()}`;

  const handleDimentionChange = (event: ChangeEvent<HTMLInputElement>, type: string) => {
    const value = event.target.value;
    type === 'row' ? setRowCount(parseInt(value)) : setColumnCount(parseInt(value));
  };

  const handleSave = () => {
    if (rowCount !== undefined && columnCount !== undefined) {
      onSaveHandler(rowCount, columnCount);
      resetForm();
    }
  };

  const handleClose = () => {
    onCloseHandler();
    resetForm();
  };

  const resetForm = () => {
    setRowCount(undefined);
    setColumnCount(undefined);
  };

  useImperativeHandle(ref, () => ({
    clearForm: resetForm,
    focusFirstField: () => firstField.current?.focus(),
  }));

  useEffect(() => {
    const handleResizeScroll = () => {
      handleClose();
    };

    setScrollableContainers(handleResizeScroll);

    window.addEventListener('scroll', handleResizeScroll);
    window.addEventListener('resize', handleResizeScroll);

    return () => {
      window.removeEventListener('scroll', handleResizeScroll);
      window.removeEventListener('resize', handleResizeScroll);
      removeScrollableContainers(handleResizeScroll);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={styles['tableform']}>
      <FormField className={styles['tableform__input-field']}>
        <Label htmlFor={rowId}>{t.table.nrofRows}</Label>
        <InputField
          id={rowId}
          placeholder={t.table.nrofRowsPlaceholder}
          value={rowCount ? rowCount : ''}
          onChange={(event) => handleDimentionChange(event, 'row')}
          aria-label={t.aria.labels.tableRowfield}
          ref={firstField}
        />
      </FormField>
      <FormField className={styles['tableform__input-field']}>
        <Label htmlFor={columnId}>{t.table.nrofColumns}</Label>
        <InputField
          id={columnId}
          placeholder={t.table.nrofColumnsPlaceholder}
          value={columnCount ? columnCount : ''}
          onChange={(event) => handleDimentionChange(event, 'column')}
          aria-label={t.aria.labels.tableColumnfield}
        />
      </FormField>
      <div className={styles['tableform__footer']}>
        <Button variant={'secondary'} onClick={handleClose} aria-label={t.common.cancel}>
          {t.common.cancel}
        </Button>
        <Button
          variant={'primary'}
          onClick={handleSave}
          disabled={!rowCount || !columnCount}
          aria-label={t.aria.labels.tableOkButton}
        >
          {t.common.add}
        </Button>
      </div>
    </div>
  );
});
